import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Autocomplete,
  AutocompleteItem,
  Button,
  Checkbox,
  Input,
  Select,
  SelectItem,
  DateField,
} from '@structural-climbing/ui'
import type { GymPlan } from '../plans/types'
import { PlanPriceFields } from '../plans/PlanPriceFields'
import { usePlanDiscount } from '../plans/usePlanDiscount'
import type { DeskPaymentMethod } from '../invoices/paymentMethod'
import { DeskMatchBlock, mergeConfirmed } from './DeskMatchBlock'
import type { DeskCandidate, DeskMatch, DeskMatchChoice } from './DeskMatchBlock'
import { DeskMemberPicker } from './DeskMemberPicker'

interface BillingForm {
  billingType: 'individual' | 'company'
  firstName: string
  lastName: string
  cnp: string
  companyName: string
  cui: string
  regCom: string
  address: string
  city: string
  county: string
}

const emptyBilling = (): BillingForm => ({
  billingType: 'individual',
  firstName: '',
  lastName: '',
  cnp: '',
  companyName: '',
  cui: '',
  regCom: '',
  address: '',
  city: '',
  county: '',
})

export interface ApproveParams {
  planId: string | null
  pricePaid: number
  paymentMethod: DeskPaymentMethod
  isTransfer: boolean
  transferStartDate: string
  transferEntriesRemaining: string
  issueInvoice: boolean
  billing: BillingForm | null
  /** True once staff confirmed the parent or guardian of a minor. */
  guardianConfirmed: boolean
  /** The desk member staff confirmed is this person, to merge on approval. */
  mergeDeskUserId: string | null
}

interface JoinRequest {
  id: string
  user_id: string | null
  user_name: string
  user_email: string
  user_date_of_birth?: string | null
  is_guest: boolean
  /** Set on a request for someone under 18: the adult who signed for them. */
  guardian_first_name?: string | null
  guardian_last_name?: string | null
  guardian_email?: string | null
  guardian_phone?: string | null
  signature_keys?: Record<string, string> | null
  added_at_desk?: boolean
  /** Someone the gym already added at the desk, with the same verified email. */
  desk_match?: DeskMatch | null
}

interface ApproveRequestModalProps {
  request: JoinRequest | null
  gymPlans: GymPlan[]
  isLoading: boolean
  onApprove: (params: ApproveParams) => void
  onClose: () => void
  /** Desk members with no email, for linking this request by hand. */
  loadDeskCandidates?: (requestId: string) => Promise<DeskCandidate[] | null>
  /** One of them, with what a merge would move. */
  loadDeskMember?: (requestId: string, userId: string) => Promise<DeskMatch | null>
}

export function ApproveRequestModal({
  request,
  gymPlans,
  isLoading,
  onApprove,
  onClose,
  loadDeskCandidates,
  loadDeskMember,
}: ApproveRequestModalProps) {
  const { t } = useTranslation()

  const today = new Date().toISOString().slice(0, 10)

  const [selectedPlanId, setSelectedPlanId] = useState('')
  const [withoutPlan, setWithoutPlan] = useState(false)
  const [pricePaid, setPricePaid] = useState('')
  const [isTransfer, setIsTransfer] = useState(false)
  const [transferStartDate, setTransferStartDate] = useState(today)
  const [transferEntriesRemaining, setTransferEntriesRemaining] = useState('')
  const [paymentMethod, setPaymentMethod] = useState<DeskPaymentMethod>('cash')
  const [issueInvoice, setIssueInvoice] = useState(false)
  const [billing, setBilling] = useState<BillingForm>(emptyBilling())
  // Never ticked for staff: it records that someone checked.
  const [guardianConfirmed, setGuardianConfirmed] = useState(false)
  const [deskChoice, setDeskChoice] = useState<DeskMatchChoice | null>(null)
  const [pickedMatch, setPickedMatch] = useState<DeskMatch | null>(null)
  // Ticked by staff when the desk entry's name or birth date differ.
  const [differencesChecked, setDifferencesChecked] = useState(false)

  const plans = request?.is_guest ? gymPlans.filter((p) => p.plan_type === 'day_pass') : gymPlans
  const selectedPlan = plans.find((p) => p.id === selectedPlanId)
  const discount = usePlanDiscount({ selectedPlan, setPricePaid })

  useEffect(() => {
    setSelectedPlanId('')
    setWithoutPlan(false)
    setPricePaid('')
    discount.reset()
    setIsTransfer(false)
    setTransferStartDate(today)
    setTransferEntriesRemaining('')
    setPaymentMethod('cash')
    setIssueInvoice(false)
    setBilling(emptyBilling())
    setGuardianConfirmed(false)
    setDeskChoice(null)
    setPickedMatch(null)
    setDifferencesChecked(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [request?.id])

  const deskMatch = request?.desk_match ?? pickedMatch
  // Only a member's own adult request can take a merge, as the Worker rules.
  const canLink =
    !!request &&
    !request.desk_match &&
    !request.is_guest &&
    !!request.user_id &&
    !request.guardian_first_name &&
    !!loadDeskCandidates &&
    !!loadDeskMember
  // Merging keeps the desk member's plan, so there is no plan to pick or sell.
  const merging = !!deskMatch && deskChoice === 'merge'
  // Until staff answer whether this is the desk member, the plan is not the
  // question yet.
  const hidePlan = merging || (!!deskMatch && deskChoice === null)
  const needsPlan = !merging && plans.length > 0 && (!!request?.user_id || !!request?.is_guest)
  // A parent who joins only for their child holds no plan of their own: the
  // child's plan is assigned from the parent's profile afterwards. A guest
  // always needs a day pass, so the choice is never offered there.
  const canSkipPlan = needsPlan && !request?.is_guest
  // A minor's request was signed by someone saying they are the parent. A
  // confirmed email shows a second person was involved, not who they are, so
  // the Worker refuses the approval until staff say they checked.
  const guardian = request?.guardian_first_name
    ? {
        name: [request.guardian_first_name, request.guardian_last_name].filter(Boolean).join(' '),
        email: request.guardian_email ?? '',
        phone: request.guardian_phone ?? '',
      }
    : null
  const canApprove =
    (!deskMatch || deskChoice !== null) &&
    (!merging || mergeConfirmed(deskMatch, differencesChecked)) &&
    (!needsPlan || !!selectedPlanId || (canSkipPlan && withoutPlan)) &&
    (!guardian || guardianConfirmed)

  function handlePlanSelect(id: string) {
    setSelectedPlanId(id)
    const plan = plans.find((p) => p.id === id)
    if (plan) setPricePaid(discount.priceFor(plan).toFixed(2))
  }

  function setBillingField(field: keyof BillingForm, value: string) {
    setBilling((prev) => ({ ...prev, [field]: value }))
  }

  function handleApprove() {
    onApprove({
      planId: selectedPlanId || null,
      pricePaid: parseFloat(pricePaid) || 0,
      paymentMethod,
      isTransfer,
      transferStartDate,
      transferEntriesRemaining,
      issueInvoice,
      billing: issueInvoice ? billing : null,
      guardianConfirmed,
      mergeDeskUserId: merging ? deskMatch.user_id : null,
    })
  }

  function handlePicked(match: DeskMatch) {
    setPickedMatch(match)
    setDeskChoice(null)
    setDifferencesChecked(false)
  }

  function handleDeskChoice(choice: DeskMatchChoice) {
    setDeskChoice(choice)
    setDifferencesChecked(false)
    if (choice === 'merge') {
      setSelectedPlanId('')
      setWithoutPlan(false)
      setPricePaid('')
      discount.reset()
      setIsTransfer(false)
      setIssueInvoice(false)
    }
  }

  return (
    <Modal
      scrollBehavior="inside"
      isOpen={request !== null}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <ModalContent>
        <ModalHeader>{t('requests.approveModalTitle')}</ModalHeader>
        <ModalBody>
          <div className="flex flex-col gap-4">
            <p className="text-sm text-foreground/60">
              {t('requests.approveModalFor', { name: request?.user_name })}
            </p>

            {deskMatch && request && (
              <DeskMatchBlock
                match={deskMatch}
                picked={!request.desk_match}
                requestName={request.user_name}
                requestDateOfBirth={request.user_date_of_birth ?? null}
                choice={deskChoice}
                onChoice={handleDeskChoice}
                differencesChecked={differencesChecked}
                onDifferencesChecked={setDifferencesChecked}
              />
            )}
            {pickedMatch && (
              <Button
                size="sm"
                variant="light"
                className="self-start"
                onPress={() => {
                  setPickedMatch(null)
                  setDeskChoice(null)
                  setDifferencesChecked(false)
                }}
              >
                {t('requests.deskLinkChange')}
              </Button>
            )}
            {canLink && !pickedMatch && (
              <DeskMemberPicker
                key={request.id}
                loadCandidates={() => loadDeskCandidates(request.id)}
                loadMember={(userId) => loadDeskMember(request.id, userId)}
                onPicked={handlePicked}
              />
            )}

            {/* Nothing was signed online, so the paper at the desk is the
                agreement. Said again here, at the moment of approving. */}
            {request &&
              !request.added_at_desk &&
              Object.keys(request.signature_keys ?? {}).length === 0 && (
                <p
                  role="alert"
                  className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm font-semibold text-danger"
                >
                  {t(
                    request.guardian_first_name
                      ? 'requests.nothingSignedMinor'
                      : 'requests.nothingSigned',
                  )}
                </p>
              )}

            {guardian && (
              <div className="flex flex-col gap-2 rounded-lg border border-warning/40 bg-warning/5 p-3">
                <p className="text-sm font-semibold">{t('requests.guardianTitle')}</p>
                <p className="text-sm">
                  {guardian.name}
                  {guardian.phone && (
                    <span className="block text-xs text-foreground/60">{guardian.phone}</span>
                  )}
                  {guardian.email && (
                    <span className="block text-xs text-foreground/60">{guardian.email}</span>
                  )}
                </p>
                <p className="text-xs text-foreground/60">{t('requests.guardianHint')}</p>
                <Checkbox isSelected={guardianConfirmed} onValueChange={setGuardianConfirmed}>
                  <span className="text-sm">{t('requests.guardianConfirm')}</span>
                </Checkbox>
              </div>
            )}

            {canSkipPlan && !hidePlan && (
              <div className="flex flex-col gap-1">
                <Checkbox
                  isSelected={withoutPlan}
                  onValueChange={(v) => {
                    setWithoutPlan(v)
                    if (v) {
                      setSelectedPlanId('')
                      setPricePaid('')
                      discount.reset()
                      setIsTransfer(false)
                      setIssueInvoice(false)
                    }
                  }}
                >
                  {t('requests.approveWithoutPlan')}
                </Checkbox>
                <p className="text-xs text-foreground/60">{t('requests.approveWithoutPlanHint')}</p>
              </div>
            )}

            {gymPlans.length > 0 && !withoutPlan && !hidePlan && (
              <Autocomplete
                label={t('requests.selectPlan')}
                selectedKey={selectedPlanId || null}
                onSelectionChange={(key) => handlePlanSelect(key ? String(key) : '')}
              >
                {plans.map((p) => (
                  <AutocompleteItem
                    key={p.id}
                    textValue={`${p.name} — ${p.price.toFixed(2)} ${p.currency}`}
                  >
                    {p.name} — {p.price.toFixed(2)} {p.currency}
                  </AutocompleteItem>
                ))}
              </Autocomplete>
            )}

            {/* A guest can be discounted too, e.g. a student on a day pass. */}
            {selectedPlan && !hidePlan && (
              <PlanPriceFields
                plan={selectedPlan}
                pricePaid={pricePaid}
                onPricePaidChange={setPricePaid}
                discount={discount}
              />
            )}

            {selectedPlan && !request?.is_guest && !hidePlan && (
              <>
                <Checkbox
                  isSelected={isTransfer}
                  onValueChange={(v) => {
                    setIsTransfer(v)
                    if (v && selectedPlan?.plan_type === 'entry_based')
                      setTransferEntriesRemaining(String(selectedPlan.max_entries ?? ''))
                    else setTransferEntriesRemaining('')
                  }}
                >
                  {t('assignPlan.transferToggle')}
                </Checkbox>
                {isTransfer && (
                  <>
                    <DateField
                      label={t('assignPlan.transferStartDateLabel')}
                      value={transferStartDate}
                      onValueChange={setTransferStartDate}
                      description={t('assignPlan.transferStartDateDescription')}
                    />
                    {selectedPlan?.plan_type === 'entry_based' && (
                      <Input
                        label={t('assignPlan.entriesRemainingLabel')}
                        type="number"
                        min="0"
                        value={transferEntriesRemaining}
                        onValueChange={setTransferEntriesRemaining}
                        description={t('assignPlan.transferEntriesRemainingDescription')}
                      />
                    )}
                  </>
                )}
              </>
            )}

            {(selectedPlan || request?.is_guest) && !isTransfer && !hidePlan && (
              <Select
                label={t('assignPlan.paymentMethodLabel')}
                selectedKeys={[paymentMethod]}
                onSelectionChange={(keys) => {
                  const val = String(Array.from(keys)[0] ?? 'cash') as DeskPaymentMethod
                  setPaymentMethod(val)
                }}
              >
                <SelectItem key="cash">{t('assignPlan.cash')}</SelectItem>
                <SelectItem key="card">{t('assignPlan.card')}</SelectItem>
                <SelectItem key="transfer">{t('assignPlan.transfer')}</SelectItem>
              </Select>
            )}

            {selectedPlan && !request?.is_guest && !isTransfer && !hidePlan && (
              <>
                <Checkbox isSelected={issueInvoice} onValueChange={setIssueInvoice}>
                  {t('assignPlan.issueInvoice')}
                </Checkbox>

                {issueInvoice && (
                  <div className="flex flex-col gap-3 rounded-lg border border-divider p-4">
                    <Select
                      label={t('assignPlan.billingTypeLabel')}
                      selectedKeys={[billing.billingType]}
                      onSelectionChange={(keys) => {
                        const val = String(Array.from(keys)[0] ?? 'individual') as
                          | 'individual'
                          | 'company'
                        setBilling((prev) => ({ ...prev, billingType: val }))
                      }}
                    >
                      <SelectItem key="individual">{t('assignPlan.individual')}</SelectItem>
                      <SelectItem key="company">{t('assignPlan.company')}</SelectItem>
                    </Select>

                    {billing.billingType === 'individual' ? (
                      <>
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            label={t('assignPlan.firstName')}
                            value={billing.firstName}
                            onValueChange={(v) => setBillingField('firstName', v)}
                          />
                          <Input
                            label={t('assignPlan.lastName')}
                            value={billing.lastName}
                            onValueChange={(v) => setBillingField('lastName', v)}
                          />
                        </div>
                        <Input
                          label={t('assignPlan.cnp')}
                          value={billing.cnp}
                          onValueChange={(v) => setBillingField('cnp', v)}
                        />
                      </>
                    ) : (
                      <>
                        <Input
                          label={t('assignPlan.companyName')}
                          value={billing.companyName}
                          onValueChange={(v) => setBillingField('companyName', v)}
                        />
                        <div className="grid grid-cols-2 gap-3">
                          <Input
                            label={t('assignPlan.cui')}
                            value={billing.cui}
                            onValueChange={(v) => setBillingField('cui', v)}
                          />
                          <Input
                            label={t('assignPlan.regCom')}
                            value={billing.regCom}
                            onValueChange={(v) => setBillingField('regCom', v)}
                          />
                        </div>
                      </>
                    )}

                    <Input
                      label={t('assignPlan.address')}
                      value={billing.address}
                      onValueChange={(v) => setBillingField('address', v)}
                    />
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        label={t('assignPlan.city')}
                        value={billing.city}
                        onValueChange={(v) => setBillingField('city', v)}
                      />
                      <Input
                        label={t('assignPlan.county')}
                        value={billing.county}
                        onValueChange={(v) => setBillingField('county', v)}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant="light" onClick={onClose} isDisabled={isLoading}>
            {t('requests.cancel')}
          </Button>
          <Button
            color="success"
            isLoading={isLoading}
            isDisabled={!canApprove || isLoading}
            onClick={handleApprove}
          >
            {t(merging ? 'requests.approveAndMerge' : 'requests.approve')}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  )
}
